import algoliasearchHelper from 'algoliasearch-helper';

import {
  checkIndexUiState,
  createDocumentationMessageGenerator,
  resolveSearchParameters,
  mergeSearchParameters,
  warning,
  isIndexWidget,
  createInitArgs,
  createRenderArgs,
} from '../../lib/utils';
import { addWidgetId } from '../../lib/utils/addWidgetId';

import type {
  InstantSearch,
  UiState,
  IndexUiState,
  Widget,
  ScopedResult,
  IndexRenderState,
  RenderOptions,
  RecommendResponse,
  SearchClient,
} from '../../types';
import type {
  AlgoliaSearchHelper as Helper,
  DerivedHelper,
  PlainSearchParameters,
  SearchParameters,
  SearchResults,
  AlgoliaSearchHelper,
  RecommendParameters,
} from 'algoliasearch-helper';

const withUsage = createDocumentationMessageGenerator({
  name: 'index-widget',
});

export type IndexWidgetParams =
  | {
      /**
       * The index or composition id to target.
       */
      indexName: string;
      /**
       * Id to use for the index if there are multiple indices with the same name.
       * This will be used to create the URL and the render state.
       */
      indexId?: string;
      /**
       * If `true`, the index will not be merged with the main helper's state.
       * This means that the index will not be part of the main search request.
       *
       * @default false
       */
      EXPERIMENTAL_isolated?: false;
    }
  | {
      /**
       * If `true`, the index will not be merged with the main helper's state.
       * This means that the index will not be part of the main search request.
       *
       * This option is EXPERIMENTAL, and implementation details may change in the future.
       * Things that could change are:
       * - which widgets get rendered when a change happens
       * - whether the index searches automatically
       * - whether the index is included in the URL / UiState
       * - whether the index is included in server-side rendering
       *
       * @default false
       */
      EXPERIMENTAL_isolated: true;
      /**
       * The index or composition id to target.
       */
      indexName?: string;
      /**
       * Id to use for the index if there are multiple indices with the same name.
       * This will be used to create the URL and the render state.
       */
      indexId?: string;
    };

export type IndexInitOptions = {
  instantSearchInstance: InstantSearch;
  parent: IndexWidget | null;
  uiState: UiState;
};

export type IndexRenderOptions = {
  instantSearchInstance: InstantSearch;
};

type WidgetSearchParametersOptions = Parameters<
  NonNullable<Widget['getWidgetSearchParameters']>
>[1];
type LocalWidgetSearchParametersOptions = WidgetSearchParametersOptions & {
  initialSearchParameters: SearchParameters;
};
type LocalWidgetRecommendParametersOptions = WidgetSearchParametersOptions & {
  initialRecommendParameters: RecommendParameters;
};

export type IndexWidgetDescription = {
  $$type: 'ais.index';
  $$widgetType: 'ais.index';
};

export type IndexWidget<TUiState extends UiState = UiState> = Omit<
  Widget<IndexWidgetDescription & { widgetParams: IndexWidgetParams }>,
  'getWidgetUiState' | 'getWidgetState'
> & {
  getIndexName: () => string;
  getIndexId: () => string;
  getHelper: () => Helper | null;
  getResults: () => SearchResults | null;
  getResultsForWidget: (
    widget: IndexWidget | Widget
  ) => SearchResults | RecommendResponse<any> | null;
  getPreviousState: () => SearchParameters | null;
  getScopedResults: () => ScopedResult[];
  getParent: () => IndexWidget | null;
  getWidgets: () => Array<Widget | IndexWidget>;
  createURL: (
    nextState: SearchParameters | ((state: IndexUiState) => IndexUiState)
  ) => string;

  addWidgets: (widgets: Array<Widget | IndexWidget | Widget[]>) => IndexWidget;
  removeWidgets: (
    widgets: Array<Widget | IndexWidget | Widget[]>
  ) => IndexWidget;

  init: (options: IndexInitOptions) => void;
  render: (options: IndexRenderOptions) => void;
  dispose: () => void;
  /**
   * @deprecated
   */
  getWidgetState: (uiState: UiState) => UiState;
  getWidgetUiState: <TSpecificUiState extends UiState = TUiState>(
    uiState: TSpecificUiState
  ) => TSpecificUiState;
  getWidgetSearchParameters: (
    searchParameters: SearchParameters,
    searchParametersOptions: { uiState: IndexUiState }
  ) => SearchParameters;
  /**
   * Set this index' UI state back to the state defined by the widgets.
   * Can only be called after `init`.
   */
  refreshUiState: () => void;
  /**
   * Set this index' UI state and search. This is the equivalent of calling
   * a spread `setUiState` on the InstantSearch instance.
   * Can only be called after `init`.
   */
  setIndexUiState: (
    indexUiState:
      | TUiState[string]
      | ((previousIndexUiState: TUiState[string]) => TUiState[string])
  ) => void;
  /**
   * This index is isolated, meaning it will not be merged with the main
   * helper's state.
   * @private
   */
  _isolated: boolean;
};

/**
 * This is the same content as helper._change / setState, but allowing for extra
 * UiState to be synchronized.
 * see: https://github.com/algolia/algoliasearch-helper-js/blob/6b835ffd07742f2d6b314022cce6848f5cfecd4a/src/algoliasearch.helper.js#L1311-L1324
 */
function privateHelperSetState(
  helper: AlgoliaSearchHelper,
  {
    state,
    recommendState,
    isPageReset,
    _uiState,
  }: {
    state: SearchParameters;
    recommendState: RecommendParameters;
    isPageReset?: boolean;
    _uiState?: IndexUiState;
  }
) {
  if (state !== helper.state) {
    helper.state = state;

    helper.emit('change', {
      state: helper.state,
      results: helper.lastResults,
      isPageReset,
      _uiState,
    });
  }

  if (recommendState !== helper.recommendState) {
    helper.recommendState = recommendState;

    // eslint-disable-next-line no-warning-comments
    // TODO: emit "change" event when events for Recommend are implemented
  }
}

type WidgetUiStateOptions = Parameters<
  NonNullable<Widget['getWidgetUiState']>
>[1];

function getLocalWidgetsUiState(
  widgets: Array<Widget | IndexWidget>,
  widgetStateOptions: WidgetUiStateOptions,
  initialUiState: IndexUiState = {}
) {
  return widgets.reduce((uiState, widget) => {
    if (isIndexWidget(widget)) {
      return uiState;
    }

    if (!widget.getWidgetUiState && !widget.getWidgetState) {
      return uiState;
    }

    if (widget.getWidgetUiState) {
      return widget.getWidgetUiState(uiState, widgetStateOptions);
    }

    return widget.getWidgetState!(uiState, widgetStateOptions);
  }, initialUiState);
}

function getLocalWidgetsSearchParameters(
  widgets: Array<Widget | IndexWidget>,
  widgetSearchParametersOptions: LocalWidgetSearchParametersOptions
): SearchParameters {
  const { initialSearchParameters, ...rest } = widgetSearchParametersOptions;

  return widgets.reduce<SearchParameters>((state, widget) => {
    if (!widget.getWidgetSearchParameters || isIndexWidget(widget)) {
      return state;
    }

    if (widget.dependsOn === 'search' && widget.getWidgetParameters) {
      return widget.getWidgetParameters(state, rest);
    }

    return widget.getWidgetSearchParameters(state, rest);
  }, initialSearchParameters);
}

function getLocalWidgetsRecommendParameters(
  widgets: Array<Widget | IndexWidget>,
  widgetRecommendParametersOptions: LocalWidgetRecommendParametersOptions
): RecommendParameters {
  const { initialRecommendParameters, ...rest } =
    widgetRecommendParametersOptions;

  return widgets.reduce((state, widget) => {
    if (
      !isIndexWidget(widget) &&
      widget.dependsOn === 'recommend' &&
      widget.getWidgetParameters
    ) {
      return widget.getWidgetParameters(state, rest);
    }
    return state;
  }, initialRecommendParameters);
}

function resetPageFromWidgets(widgets: Array<Widget | IndexWidget>): void {
  const indexWidgets = widgets.filter(isIndexWidget);

  if (indexWidgets.length === 0) {
    return;
  }

  indexWidgets.forEach((widget) => {
    const widgetHelper = widget.getHelper()!;

    privateHelperSetState(widgetHelper, {
      state: widgetHelper.state.resetPage(),
      recommendState: widgetHelper.recommendState,
      isPageReset: true,
    });

    resetPageFromWidgets(widget.getWidgets());
  });
}

function resolveScopedResultsFromWidgets(
  widgets: Array<Widget | IndexWidget>
): ScopedResult[] {
  const indexWidgets = widgets.filter(isIndexWidget);

  return indexWidgets.reduce<ScopedResult[]>((scopedResults, current) => {
    return scopedResults.concat(
      {
        indexId: current.getIndexId(),
        results: current.getResults()!,
        helper: current.getHelper()!,
      },
      ...resolveScopedResultsFromWidgets(current.getWidgets())
    );
  }, []);
}

const index = (widgetParams: IndexWidgetParams): IndexWidget => {
  if (
    widgetParams === undefined ||
    (widgetParams.indexName === undefined &&
      !widgetParams.EXPERIMENTAL_isolated)
  ) {
    throw new Error(withUsage('The `indexName` option is required.'));
  }

  // When isolated=true, we use an empty string as the default indexName.
  // This is intentional: isolated indices do not require a real index name.
  const {
    indexName = '',
    indexId = indexName,
    EXPERIMENTAL_isolated: isolated = false,
  } = widgetParams;

  let localWidgets: Array<Widget | IndexWidget> = [];
  let localUiState: IndexUiState = {};
  let localInstantSearchInstance: InstantSearch | null = null;
  let localParent: IndexWidget | null = null;
  let helper: Helper | null = null;
  let derivedHelper: DerivedHelper | null = null;
  let lastValidSearchParameters: SearchParameters | null = null;
  let hasRecommendWidget: boolean = false;
  let hasSearchWidget: boolean = false;

  return {
    $$type: 'ais.index',
    $$widgetType: 'ais.index',

    _isolated: isolated,

    getIndexName() {
      return indexName;
    },

    getIndexId() {
      return indexId;
    },

    getHelper() {
      return helper;
    },

    getResults() {
      if (!derivedHelper?.lastResults) return null;

      // To make the UI optimistic, we patch the state to display to the current
      // one instead of the one associated with the latest results.
      // This means user-driven UI changes (e.g., checked checkbox) are reflected
      // immediately instead of waiting for Algolia to respond, regardless of
      // the status of the network request.
      derivedHelper.lastResults._state = helper!.state;

      return derivedHelper.lastResults;
    },

    getResultsForWidget(widget) {
      if (
        widget.dependsOn !== 'recommend' ||
        isIndexWidget(widget) ||
        widget.$$id === undefined
      ) {
        return this.getResults();
      }

      if (!helper?.lastRecommendResults) {
        return null;
      }

      return helper.lastRecommendResults[widget.$$id];
    },

    getPreviousState() {
      return lastValidSearchParameters;
    },

    getScopedResults() {
      const widgetParent = this.getParent();
      let widgetSiblings;

      if (widgetParent) {
        widgetSiblings = widgetParent.getWidgets();
      } else if (indexName.length === 0) {
        // The widget is the root but has no index name:
        // we resolve results from its children index widgets
        widgetSiblings = this.getWidgets();
      } else {
        // The widget is the root and has an index name:
        // we consider itself as the only sibling
        widgetSiblings = [this];
      }

      return resolveScopedResultsFromWidgets(widgetSiblings);
    },

    getParent() {
      return isolated ? null : localParent;
    },

    createURL(
      nextState: SearchParameters | ((state: IndexUiState) => IndexUiState)
    ) {
      if (typeof nextState === 'function') {
        return localInstantSearchInstance!._createURL({
          [indexId]: nextState(localUiState),
        });
      }
      return localInstantSearchInstance!._createURL({
        [indexId]: getLocalWidgetsUiState(localWidgets, {
          searchParameters: nextState,
          helper: helper!,
        }),
      });
    },

    getWidgets() {
      return localWidgets;
    },

    addWidgets(widgets) {
      if (!Array.isArray(widgets)) {
        throw new Error(
          withUsage('The `addWidgets` method expects an array of widgets.')
        );
      }
      const flatWidgets = widgets.reduce<Array<Widget | IndexWidget>>(
        (acc, w) => acc.concat(Array.isArray(w) ? w : [w]),
        []
      );

      if (
        flatWidgets.some(
          (widget) =>
            typeof widget.init !== 'function' &&
            typeof widget.render !== 'function'
        )
      ) {
        throw new Error(
          withUsage(
            'The widget definition expects a `render` and/or an `init` method.'
          )
        );
      }

      flatWidgets.forEach((widget) => {
        if (isIndexWidget(widget)) {
          return;
        }

        if (localInstantSearchInstance && widget.dependsOn === 'recommend') {
          localInstantSearchInstance._hasRecommendWidget = true;
        } else if (localInstantSearchInstance) {
          localInstantSearchInstance._hasSearchWidget = true;
        } else if (widget.dependsOn === 'recommend') {
          hasRecommendWidget = true;
        } else {
          hasSearchWidget = true;
        }

        addWidgetId(widget);
      });

      localWidgets = localWidgets.concat(flatWidgets);
      if (localInstantSearchInstance && Boolean(flatWidgets.length)) {
        privateHelperSetState(helper!, {
          state: getLocalWidgetsSearchParameters(localWidgets, {
            uiState: localUiState,
            initialSearchParameters: helper!.state,
          }),
          recommendState: getLocalWidgetsRecommendParameters(localWidgets, {
            uiState: localUiState,
            initialRecommendParameters: helper!.recommendState,
          }),
          _uiState: localUiState,
        });

        // We compute the render state before calling `init` in a separate loop
        // to construct the whole render state object that is then passed to
        // `init`.
        flatWidgets.forEach((widget) => {
          if (widget.getRenderState) {
            const renderState = widget.getRenderState(
              localInstantSearchInstance!.renderState[this.getIndexId()] || {},
              createInitArgs(
                localInstantSearchInstance!,
                this,
                localInstantSearchInstance!._initialUiState
              )
            );

            storeRenderState({
              renderState,
              instantSearchInstance: localInstantSearchInstance!,
              parent: this,
            });
          }
        });

        flatWidgets.forEach((widget) => {
          if (widget.init) {
            widget.init(
              createInitArgs(
                localInstantSearchInstance!,
                this,
                localInstantSearchInstance!._initialUiState
              )
            );
          }
        });

        if (isolated) {
          helper?.search();
        } else {
          localInstantSearchInstance.scheduleSearch();
        }
      }

      return this;
    },

    removeWidgets(widgets) {
      if (!Array.isArray(widgets)) {
        throw new Error(
          withUsage('The `removeWidgets` method expects an array of widgets.')
        );
      }
      const flatWidgets = widgets.reduce<Array<Widget | IndexWidget>>(
        (acc, w) => acc.concat(Array.isArray(w) ? w : [w]),
        []
      );

      if (flatWidgets.some((widget) => typeof widget.dispose !== 'function')) {
        throw new Error(
          withUsage('The widget definition expects a `dispose` method.')
        );
      }

      localWidgets = localWidgets.filter(
        (widget) => flatWidgets.indexOf(widget) === -1
      );

      localWidgets.forEach((widget) => {
        if (isIndexWidget(widget)) {
          return;
        }

        if (localInstantSearchInstance && widget.dependsOn === 'recommend') {
          localInstantSearchInstance._hasRecommendWidget = true;
        } else if (localInstantSearchInstance) {
          localInstantSearchInstance._hasSearchWidget = true;
        } else if (widget.dependsOn === 'recommend') {
          hasRecommendWidget = true;
        } else {
          hasSearchWidget = true;
        }
      });

      if (localInstantSearchInstance && Boolean(flatWidgets.length)) {
        const { cleanedSearchState, cleanedRecommendState } =
          flatWidgets.reduce(
            (states, widget) => {
              // the `dispose` method exists at this point we already assert it
              const next = widget.dispose!({
                helper: helper!,
                state: states.cleanedSearchState,
                recommendState: states.cleanedRecommendState,
                parent: this,
              });

              if (next instanceof algoliasearchHelper.RecommendParameters) {
                states.cleanedRecommendState = next;
              } else if (next) {
                states.cleanedSearchState = next;
              }

              return states;
            },
            {
              cleanedSearchState: helper!.state,
              cleanedRecommendState: helper!.recommendState,
            }
          );

        const newState = localInstantSearchInstance.future
          .preserveSharedStateOnUnmount
          ? getLocalWidgetsSearchParameters(localWidgets, {
              uiState: localUiState,
              initialSearchParameters: new algoliasearchHelper.SearchParameters(
                {
                  index: this.getIndexName(),
                }
              ),
            })
          : getLocalWidgetsSearchParameters(localWidgets, {
              uiState: getLocalWidgetsUiState(localWidgets, {
                searchParameters: cleanedSearchState,
                helper: helper!,
              }),
              initialSearchParameters: cleanedSearchState,
            });

        localUiState = getLocalWidgetsUiState(localWidgets, {
          searchParameters: newState,
          helper: helper!,
        });

        helper!.setState(newState);
        helper!.recommendState = cleanedRecommendState;

        if (localWidgets.length) {
          if (isolated) {
            helper?.search();
          } else {
            localInstantSearchInstance.scheduleSearch();
          }
        }
      }

      return this;
    },

    init({ instantSearchInstance, parent, uiState }: IndexInitOptions) {
      if (helper !== null) {
        // helper is already initialized, therefore we do not need to set up
        // any listeners
        return;
      }

      localInstantSearchInstance = instantSearchInstance;
      localParent = parent;
      localUiState = uiState[indexId] || {};

      // The `mainHelper` is already defined at this point. The instance is created
      // inside InstantSearch at the `start` method, which occurs before the `init`
      // step.
      const mainHelper = instantSearchInstance.mainHelper!;
      const parameters = getLocalWidgetsSearchParameters(localWidgets, {
        uiState: localUiState,
        initialSearchParameters: new algoliasearchHelper.SearchParameters({
          index: indexName,
        }),
      });
      const recommendParameters = getLocalWidgetsRecommendParameters(
        localWidgets,
        {
          uiState: localUiState,
          initialRecommendParameters:
            new algoliasearchHelper.RecommendParameters(),
        }
      );

      // This Helper is only used for state management we do not care about the
      // `searchClient`. Only the "main" Helper created at the `InstantSearch`
      // level is aware of the client.
      helper = algoliasearchHelper(
        mainHelper.getClient(),
        parameters.index,
        parameters
      );
      helper.recommendState = recommendParameters;

      // We forward the call to `search` to the "main" instance of the Helper
      // which is responsible for managing the queries (it's the only one that is
      // aware of the `searchClient`).
      helper.search = () => {
        if (isolated) {
          instantSearchInstance.status = 'loading';
          this.render({ instantSearchInstance });
          return instantSearchInstance.compositionID
            ? helper!.searchWithComposition()
            : helper!.searchOnlyWithDerivedHelpers();
        }

        if (instantSearchInstance.onStateChange) {
          instantSearchInstance.onStateChange({
            uiState: instantSearchInstance.mainIndex.getWidgetUiState({}),
            setUiState: (nextState) =>
              instantSearchInstance.setUiState(nextState, false),
          });

          // We don't trigger a search when controlled because it becomes the
          // responsibility of `setUiState`.
          return mainHelper;
        }

        return mainHelper.search();
      };

      helper.searchWithoutTriggeringOnStateChange = () => {
        return mainHelper.search();
      };

      // We use the same pattern for the `searchForFacetValues`.
      helper.searchForFacetValues = (
        facetName,
        facetValue,
        maxFacetHits,
        userState: PlainSearchParameters
      ) => {
        const state = helper!.state.setQueryParameters(userState);

        return mainHelper.searchForFacetValues(
          facetName,
          facetValue,
          maxFacetHits,
          state
        );
      };

      const isolatedHelper = indexName
        ? helper
        : algoliasearchHelper({} as SearchClient, '__empty_index__', {});
      const derivingHelper = isolated
        ? isolatedHelper
        : nearestIsolatedHelper(parent, mainHelper);

      derivedHelper = derivingHelper.derive(
        () =>
          mergeSearchParameters(
            mainHelper.state,
            ...resolveSearchParameters(this)
          ),
        () => this.getHelper()!.recommendState
      );

      const indexInitialResults =
        instantSearchInstance._initialResults?.[this.getIndexId()];

      if (indexInitialResults?.results) {
        // We restore the shape of the results provided to the instance to respect
        // the helper's structure.
        const results = new algoliasearchHelper.SearchResults(
          new algoliasearchHelper.SearchParameters(indexInitialResults.state),
          indexInitialResults.results
        );

        derivedHelper.lastResults = results;
        helper.lastResults = results;
      }

      if (indexInitialResults?.recommendResults) {
        const recommendResults = new algoliasearchHelper.RecommendResults(
          new algoliasearchHelper.RecommendParameters({
            params: indexInitialResults.recommendResults.params,
          }),
          indexInitialResults.recommendResults.results
        );
        derivedHelper.lastRecommendResults = recommendResults;
        helper.lastRecommendResults = recommendResults;
      }

      // Subscribe to the Helper state changes for the page before widgets
      // are initialized. This behavior mimics the original one of the Helper.
      // It makes sense to replicate it at the `init` step. We have another
      // listener on `change` below, once `init` is done.
      helper.on('change', ({ isPageReset }) => {
        if (isPageReset) {
          resetPageFromWidgets(localWidgets);
        }
      });

      derivedHelper.on('search', () => {
        // The index does not manage the "staleness" of the search. This is the
        // responsibility of the main instance. It does not make sense to manage
        // it at the index level because it's either: all of them or none of them
        // that are stalled. The queries are performed into a single network request.
        instantSearchInstance.scheduleStalledRender();

        if (__DEV__) {
          checkIndexUiState({ index: this, indexUiState: localUiState });
        }
      });

      derivedHelper.on('result', ({ results }) => {
        // The index does not render the results it schedules a new render
        // to let all the other indices emit their own results. It allows us to
        // run the render process in one pass.
        instantSearchInstance.scheduleRender();

        // the derived helper is the one which actually searches, but the helper
        // which is exposed e.g. via instance.helper, doesn't search, and thus
        // does not have access to lastResults, which it used to in pre-federated
        // search behavior.
        helper!.lastResults = results;
        lastValidSearchParameters = results?._state;
      });

      // eslint-disable-next-line no-warning-comments
      // TODO: listen to "result" event when events for Recommend are implemented
      derivedHelper.on('recommend:result', ({ recommend }) => {
        // The index does not render the results it schedules a new render
        // to let all the other indices emit their own results. It allows us to
        // run the render process in one pass.
        instantSearchInstance.scheduleRender();

        // the derived helper is the one which actually searches, but the helper
        // which is exposed e.g. via instance.helper, doesn't search, and thus
        // does not have access to lastRecommendResults.
        helper!.lastRecommendResults = recommend.results;
      });

      // We compute the render state before calling `init` in a separate loop
      // to construct the whole render state object that is then passed to
      // `init`.
      localWidgets.forEach((widget) => {
        if (widget.getRenderState) {
          const renderState = widget.getRenderState(
            instantSearchInstance.renderState[this.getIndexId()] || {},
            createInitArgs(instantSearchInstance, this, uiState)
          );

          storeRenderState({
            renderState,
            instantSearchInstance,
            parent: this,
          });
        }
      });

      localWidgets.forEach((widget) => {
        warning(
          // if it has NO getWidgetState or if it has getWidgetUiState, we don't warn
          // aka we warn if there's _only_ getWidgetState
          !widget.getWidgetState || Boolean(widget.getWidgetUiState),
          'The `getWidgetState` method is renamed `getWidgetUiState` and will no longer exist under that name in InstantSearch.js 5.x. Please use `getWidgetUiState` instead.'
        );

        if (widget.init) {
          widget.init(createInitArgs(instantSearchInstance, this, uiState));
        }
      });

      // Subscribe to the Helper state changes for the `uiState` once widgets
      // are initialized. Until the first render, state changes are part of the
      // configuration step. This is mainly for backward compatibility with custom
      // widgets. When the subscription happens before the `init` step, the (static)
      // configuration of the widget is pushed in the URL. That's what we want to avoid.
      // https://github.com/algolia/instantsearch/pull/994/commits/4a672ae3fd78809e213de0368549ef12e9dc9454
      helper.on('change', (event) => {
        const { state } = event;

        const _uiState = (event as any)._uiState;

        localUiState = getLocalWidgetsUiState(
          localWidgets,
          {
            searchParameters: state,
            helper: helper!,
          },
          _uiState || {}
        );

        // We don't trigger an internal change when controlled because it
        // becomes the responsibility of `setUiState`.
        if (!instantSearchInstance.onStateChange) {
          instantSearchInstance.onInternalStateChange();
        }
      });

      if (indexInitialResults) {
        // If there are initial results, we're not notified of the next results
        // because we don't trigger an initial search. We therefore need to directly
        // schedule a render that will render the results injected on the helper.
        instantSearchInstance.scheduleRender();
      }

      if (hasRecommendWidget) {
        instantSearchInstance._hasRecommendWidget = true;
      }
      if (hasSearchWidget) {
        instantSearchInstance._hasSearchWidget = true;
      }
    },

    render({ instantSearchInstance }: IndexRenderOptions) {
      // we can't attach a listener to the error event of search, as the error
      // then would no longer be thrown for global handlers.
      if (
        instantSearchInstance.status === 'error' &&
        !instantSearchInstance.mainHelper!.hasPendingRequests() &&
        lastValidSearchParameters
      ) {
        helper!.setState(lastValidSearchParameters);
      }

      // We only render index widgets if there are no results.
      // This makes sure `render` is never called with `results` being `null`.
      // If it's an isolated index without an index name, we render all widgets,
      // as there are no results to display for the isolated index itself.
      let widgetsToRender =
        this.getResults() ||
        derivedHelper?.lastRecommendResults ||
        (isolated && !indexName)
          ? localWidgets
          : localWidgets.filter(isIndexWidget);

      widgetsToRender = widgetsToRender.filter((widget) => {
        if (!widget.shouldRender) {
          return true;
        }

        return widget.shouldRender({ instantSearchInstance });
      });

      widgetsToRender.forEach((widget) => {
        if (widget.getRenderState) {
          const renderState = widget.getRenderState(
            instantSearchInstance.renderState[this.getIndexId()] || {},
            createRenderArgs(
              instantSearchInstance,
              this,
              widget
            ) as RenderOptions
          );

          storeRenderState({
            renderState,
            instantSearchInstance,
            parent: this,
          });
        }
      });

      widgetsToRender.forEach((widget) => {
        // At this point, all the variables used below are set. Both `helper`
        // and `derivedHelper` have been created at the `init` step. The attribute
        // `lastResults` might be `null` though. It's possible that a stalled render
        // happens before the result e.g with a dynamically added index the request might
        // be delayed. The render is triggered for the complete tree but some parts do
        // not have results yet.

        if (widget.render) {
          widget.render(
            createRenderArgs(
              instantSearchInstance,
              this,
              widget
            ) as RenderOptions
          );
        }
      });
    },

    dispose() {
      localWidgets.forEach((widget) => {
        if (widget.dispose && helper) {
          // The dispose function is always called once the instance is started
          // (it's an effect of `removeWidgets`). The index is initialized and
          // the Helper is available. We don't care about the return value of
          // `dispose` because the index is removed. We can't call `removeWidgets`
          // because we want to keep the widgets on the instance, to allow idempotent
          // operations on `add` & `remove`.
          widget.dispose({
            helper,
            state: helper.state,
            recommendState: helper.recommendState,
            parent: this,
          });
        }
      });

      localInstantSearchInstance = null;
      localParent = null;
      helper?.removeAllListeners();
      helper = null;

      derivedHelper?.detach();
      derivedHelper = null;
    },

    getWidgetUiState<TUiState extends UiState = UiState>(uiState: TUiState) {
      return localWidgets
        .filter(isIndexWidget)
        .filter((w) => !w._isolated)
        .reduce<TUiState>(
          (previousUiState, innerIndex) =>
            innerIndex.getWidgetUiState(previousUiState),
          {
            ...uiState,
            [indexId]: {
              ...uiState[indexId],
              ...localUiState,
            },
          }
        );
    },

    getWidgetState(uiState: UiState) {
      warning(
        false,
        'The `getWidgetState` method is renamed `getWidgetUiState` and will no longer exist under that name in InstantSearch.js 5.x. Please use `getWidgetUiState` instead.'
      );

      return this.getWidgetUiState(uiState);
    },

    getWidgetSearchParameters(searchParameters, { uiState }) {
      return getLocalWidgetsSearchParameters(localWidgets, {
        uiState,
        initialSearchParameters: searchParameters,
      });
    },

    refreshUiState() {
      localUiState = getLocalWidgetsUiState(
        localWidgets,
        {
          searchParameters: this.getHelper()!.state,
          helper: this.getHelper()!,
        },
        localUiState
      );
    },

    setIndexUiState<TIndexUiState extends IndexUiState = IndexUiState>(
      indexUiState:
        | TIndexUiState
        | ((previousIndexUiState: TIndexUiState) => TIndexUiState)
    ) {
      const nextIndexUiState =
        typeof indexUiState === 'function'
          ? indexUiState(localUiState as TIndexUiState)
          : indexUiState;

      localInstantSearchInstance!.setUiState((state) => ({
        ...state,
        [indexId]: nextIndexUiState,
      }));
    },
  };
};

export default index;

function storeRenderState({
  renderState,
  instantSearchInstance,
  parent,
}: {
  renderState: IndexRenderState;
  instantSearchInstance: InstantSearch;
  parent?: IndexWidget;
}) {
  const parentIndexName = parent
    ? parent.getIndexId()
    : instantSearchInstance.mainIndex.getIndexId();

  instantSearchInstance.renderState = {
    ...instantSearchInstance.renderState,
    [parentIndexName]: {
      ...instantSearchInstance.renderState[parentIndexName],
      ...renderState,
    },
  };
}

/**
 * Walk up the parent chain to find the closest isolated index, or fall back to mainHelper
 */
function nearestIsolatedHelper(
  current: IndexWidget | null,
  mainHelper: Helper
): Helper {
  while (current) {
    if (current._isolated) {
      return current.getHelper()!;
    }
    current = current.getParent();
  }
  return mainHelper;
}
